import type { Cents } from "../domain/money";
import { formatCents } from "../domain/money";
import type { ReportIdentity, ReportModel, ReportRow, ReportSection, ReportValue } from "./reportModel";

/**
 * PROPOSED COST ESTIMATE (Phase 13, spec §42C).
 *
 * The estimate a lender, partner or the owner actually reads: Room → Feature
 * → cost, with an optional line-level breakdown. It never says just
 * "Estimate": several financial states exist at once, so every run states
 * WHICH one it is — the current working estimate or the frozen approved
 * baseline — and which design it came from.
 *
 * Pure. Every amount is passed in from the estimate that already exists;
 * this file adds nothing but arrangement.
 */

export type EstimateBasis = "working" | "baseline";

export interface EstimateLineInput {
  featureId: string | null;
  featureName: string;
  roomName: string;
  description: string;
  amountCents: Cents;
  costType: "material" | "labor" | "other" | null;
  quantityX100: number | null;
  quantityUnit: string | null;
  unitCostCents: Cents | null;
  productName: string;
  productUrl?: string | null;
  supplierName?: string | null;
}

export interface EstimateReportInput {
  identity: ReportIdentity;
  basis: EstimateBasis;
  detail: "summary" | "detailed";
  designName: string;
  designRole: string;
  /** Baseline runs only: which baseline and when it was approved. */
  baselineName?: string;
  baselineApprovedAt?: string;
  contingencyCents: Cents;
  estimatingTaxPctX100: number | null;
  lines: EstimateLineInput[];
  /** Working runs only: the authorized budget, for context. */
  authorizedCents?: Cents | null;
  generatedAt: string;
}

const money = (cents: Cents): ReportValue => ({ kind: "money", cents });
const empty: ReportValue = { kind: "empty" };

export function quantityLabel(quantityX100: number | null, unit: string | null): string | null {
  if (quantityX100 === null || unit === null || unit === "") return null;
  const value = quantityX100 / 100;
  const text = Number.isInteger(value) ? String(value) : value.toFixed(2);
  return `${text} ${unit}`;
}

/**
 * THE QUANTITY OF A COLLAPSED ROW IS THE SUM, OR NOTHING (spec §84-6).
 *
 * A room's rows are keyed by the work's NAME, so fifteen "Install Cabinets"
 * items become one row. Its money is their total, so its quantity must be
 * their total too — printing the FIRST item's figure beside all fifteen
 * items' money said "2.50 lf · $13,132.00", which reads as $5,252 a foot.
 *
 * The rule is the one the bid sheet already follows: a quantity is stated
 * only when EVERY item states one in the SAME unit; a partial figure would
 * read as the whole. Within one item the assembly's components repeat the
 * driving quantity, so items are summed — never lines.
 */
export function collapsedQuantity(lines: EstimateLineInput[]): string | null {
  const itemKey = (line: EstimateLineInput): string => line.featureId ?? `~${line.description}`;
  const items = new Set(lines.map(itemKey));
  const driving = new Map<string, EstimateLineInput>();
  for (const line of lines) {
    const key = itemKey(line);
    if (!driving.has(key) && quantityLabel(line.quantityX100, line.quantityUnit) !== null) {
      driving.set(key, line);
    }
  }
  // An item that states no quantity makes the sum a lie about the rest.
  if (driving.size === 0 || driving.size !== items.size) return null;
  const units = new Set([...driving.values()].map((line) => line.quantityUnit));
  if (units.size !== 1) return null; // 12 lf + 3 each is not 15 of anything
  const total = [...driving.values()].reduce((sum, line) => sum + (line.quantityX100 ?? 0), 0);
  return quantityLabel(total, [...units][0]);
}

/** How a collapsed row describes itself: how many, and how much of it. */
export function collapsedNote(lines: EstimateLineInput[]): string | undefined {
  const items = new Set(lines.map((line) => line.featureId ?? `~${line.description}`));
  const quantity = collapsedQuantity(lines);
  if (items.size < 2) return quantity ?? undefined;
  return [`${items.size} items`, quantity].filter((part): part is string => Boolean(part)).join(" · ");
}

export function proposedEstimateReport(input: EstimateReportInput): ReportModel {
  const byRoom = new Map<string, Map<string, EstimateLineInput[]>>();
  for (const line of input.lines) {
    const roomKey = line.roomName || "Project-wide";
    const featureKey = line.featureName || line.description || "Other scope";
    if (!byRoom.has(roomKey)) byRoom.set(roomKey, new Map());
    const features = byRoom.get(roomKey)!;
    if (!features.has(featureKey)) features.set(featureKey, []);
    features.get(featureKey)!.push(line);
  }

  const sections: ReportSection[] = [];
  let scopeTotal = 0;
  for (const [roomName, features] of [...byRoom.entries()].sort(sortRooms)) {
    const rows: ReportRow[] = [];
    let roomTotal = 0;
    for (const [featureName, lines] of features) {
      const featureTotal = lines.reduce((sum, line) => sum + line.amountCents, 0);
      roomTotal += featureTotal;
      rows.push({
        label: featureName,
        note: collapsedNote(lines),
        values: [money(featureTotal)],
        emphasis: input.detail === "detailed" ? "subtotal" : undefined,
      });
      if (input.detail !== "detailed") continue;
      for (const line of lines) {
        const parts = [
          basisLabel(line.costType),
          quantityLabel(line.quantityX100, line.quantityUnit),
          line.unitCostCents !== null ? `${formatCents(line.unitCostCents)} per unit` : null,
          line.productName || null,
        ].filter((part): part is string => Boolean(part));
        rows.push({
          label: line.description,
          note: parts.join(" · ") || undefined,
          depth: 1,
          values: [money(line.amountCents)],
          link: line.productUrl ? { url: line.productUrl, text: line.productName || undefined } : undefined,
        });
      }
    }
    rows.push({ label: `${roomName} total`, values: [money(roomTotal)], emphasis: "subtotal" });
    scopeTotal += roomTotal;
    sections.push({ title: roomName, columnLabel: "Scope", columns: ["Amount"], rows });
  }

  const totalRows: ReportRow[] = [
    { label: "Scope subtotal", values: [money(scopeTotal)] },
    {
      label: "Contingency",
      note: input.basis === "baseline" ? "Frozen with the baseline." : "Set on the project; frozen when a baseline is created.",
      values: [money(input.contingencyCents)],
    },
    {
      label: input.basis === "baseline" ? "Authorized at approval" : "Total proposed",
      values: [money(scopeTotal + input.contingencyCents)],
      emphasis: "total",
    },
  ];
  if (input.basis === "working" && input.authorizedCents !== undefined) {
    totalRows.push({
      label: "Currently authorized budget",
      note:
        input.authorizedCents === null
          ? "No baseline has been approved, so nothing is authorized yet."
          : "Baseline plus approved change orders — the figure this estimate is measured against.",
      values: [input.authorizedCents === null ? empty : money(input.authorizedCents)],
    });
  }
  sections.push({ title: "Estimate total", columnLabel: "", columns: ["Amount"], rows: totalRows });

  const stateLine =
    input.basis === "baseline"
      ? `APPROVED BASELINE${input.baselineName ? ` — ${input.baselineName}` : ""}${
          input.baselineApprovedAt ? ` (approved ${input.baselineApprovedAt.slice(0, 10)})` : ""
        }`
      : "CURRENT WORKING ESTIMATE — not an approved or authorized figure";

  const disclosures = [
    input.basis === "baseline"
      ? "These are the frozen figures captured when the baseline was approved. Later scope edits, price changes and new offers never move them."
      : "This is the working estimate as it stands today. It changes as scope, quantities and prices change, and it is not what has been authorized.",
    `Design: ${input.designName} (${input.designRole}).`,
  ];
  if (input.estimatingTaxPctX100 !== null) {
    disclosures.push(
      `Estimating sales tax of ${(input.estimatingTaxPctX100 / 100).toFixed(2)}% is already included in the taxable material amounts.`,
    );
  }

  return {
    id: `proposed-estimate-${input.basis}-${input.detail}`,
    title: input.basis === "baseline" ? "Approved baseline estimate" : "Proposed cost estimate",
    subtitle: stateLine,
    identity: input.identity,
    optionSummary: `${input.detail === "detailed" ? "Detailed" : "Summary"} · ${input.designName}`,
    sections,
    disclosures,
    generatedAt: input.generatedAt,
  };
}

function basisLabel(costType: "material" | "labor" | "other" | null): string | null {
  if (costType === null) return null;
  return costType === "material" ? "Material" : costType === "labor" ? "Labor" : "Other";
}

/** Project-wide scope reads last; rooms otherwise sort by name. */
function sortRooms(a: [string, unknown], b: [string, unknown]): number {
  if (a[0] === "Project-wide") return 1;
  if (b[0] === "Project-wide") return -1;
  return a[0].localeCompare(b[0]);
}
